// apps/client/src/hooks/useBrokerTargetHistory.ts
// Period-by-period attainment for a broker's targets, active and retired,
// from the `getBrokerTargetHistory` callable.

import { useState, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { CommissionComponent } from './useBrokerTargets';

export type TargetHistoryStatus = 'met' | 'missed' | 'in_progress' | 'awaiting_data' | 'not_allocated';

export interface TargetHistoryRow {
  targetId: string;
  /** First month of the period, 'YYYY-MM'. */
  periodStart: string;
  periodLabel: string;
  status: TargetHistoryStatus;
  /** null while awaiting data. */
  valueCents: number | null;
  /** Distinct loans settled in the period (settlement_volume only). */
  loanCount: number | null;
  thresholdCents: number;
  bonusCents: number;
  metTierIndex: number;
  percentComplete: number | null;
}

export interface TargetHistoryTarget {
  targetId: string;
  label: string;
  type: string;
  period: 'monthly' | 'quarterly';
  isActive: boolean;
  effectiveFromMonth?: string;
  retiredMonth?: string;
  commissionComponents?: CommissionComponent[];
  loanBookLabel?: string;
  assistantDisplayName?: string;
  analystDisplayName?: string;
  _crossBrokerSource?: string;
  /** Analyst working several brokers: history covers this broker only. */
  thisBrokerOnly?: boolean;
}

export interface TargetHistorySkipped {
  targetId: string;
  label: string;
  reason: 'point_in_time';
}

interface UseBrokerTargetHistoryReturn {
  rows: TargetHistoryRow[];
  targets: TargetHistoryTarget[];
  skipped: TargetHistorySkipped[];
  latestDataMonth: string | null;
  /** Retired targets beyond the backend's cap, not shown. */
  omittedRetiredCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBrokerTargetHistory(
  companyId: string | undefined,
  brokerDocId: string | undefined
): UseBrokerTargetHistoryReturn {
  const [rows, setRows] = useState<TargetHistoryRow[]>([]);
  const [targets, setTargets] = useState<TargetHistoryTarget[]>([]);
  const [skipped, setSkipped] = useState<TargetHistorySkipped[]>([]);
  const [latestDataMonth, setLatestDataMonth] = useState<string | null>(null);
  const [omittedRetiredCount, setOmittedRetiredCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const fetchHistory = useCallback(async () => {
    const seq = ++requestSeq.current;
    if (!companyId || !brokerDocId) {
      setRows([]);
      setTargets([]);
      setSkipped([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const getHistory = httpsCallable(functions, 'getBrokerTargetHistory');
      const result = await getHistory({ companyId, brokerDocId });
      if (seq !== requestSeq.current) return;
      const data = result.data as {
        rows?: TargetHistoryRow[];
        targets?: TargetHistoryTarget[];
        skipped?: TargetHistorySkipped[];
        latestDataMonth?: string | null;
        omittedRetiredCount?: number;
      };
      setRows(data.rows || []);
      setTargets(data.targets || []);
      setSkipped(data.skipped || []);
      setLatestDataMonth(data.latestDataMonth ?? null);
      setOmittedRetiredCount(data.omittedRetiredCount ?? 0);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('[useBrokerTargetHistory] fetch error:', err);
      // Surfaced, not swallowed: an empty table must never pass for
      // "no history".
      setError(err instanceof Error ? err.message : 'Failed to load target history');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [companyId, brokerDocId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    rows, targets, skipped, latestDataMonth, omittedRetiredCount, loading, error, refetch: fetchHistory,
  };
}
